import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { MenuItem, Icon, IconButton } from "@material-ui/core";

import getLogger, { LogGroup } from "js/core/logger";
import { getStaticUrl } from "js/config";
import { Key } from "js/core/utilities/keys";
import { FlexLayout } from "js/Components/FlexLayout";
import { TextContent } from "js/Components/TextContent";
import { TextField } from "js/Components/TextField";
import { Popup, PopupPreview, PopupContent } from "js/Components/Popup";
import { themeColors } from "js/react/sharedStyles";

import { PopupContainerStyles } from "./PaneComponents";

const logger = getLogger(LogGroup.AI);

const InputContainer = styled.div<{ isValidSearch?: boolean }>`
    width: 100%;
    padding: 0 5px;

    div.MuiFormControl-root {
        width: 100%;
    }

    input.MuiInputBase-input {
        padding: 0;
        color: ${themeColors.darkGray};
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 125%;
    }

    input.MuiInputBase-input::placeholder {
        color: ${themeColors.darkGray};
        font-size: 14px;
        font-style: italic;
        font-weight: 400;
        line-height: 125%;
    }
`;

export default function LanguagesPopup(props: {
    selectedLanguage: string;
    onSelectedLanguageChange: (language: string) => void;
    openedForTranslation?: boolean;
}) {
    const { selectedLanguage, onSelectedLanguageChange, openedForTranslation } = props;

    const popupRef = useRef<Popup>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const [allLanguages, setAllLanguages] = useState<string[]>([]);
    const [filteredLanguages, setFilteredLanguages] = useState<string[]>([]);
    const [highlightedLanguage, setHighlightedLanguage] = useState<string | null>(null);
    const [showSearchInput, setShowSearchInput] = useState<boolean>(!!openedForTranslation);
    const [searchQuery, setSearchQuery] = useState<string>("");

    useEffect(() => {
        const fetchLanguages = async () => {
            try {
                const response = await fetch(getStaticUrl("/maps/language-list.json"));
                const data = await response.json();

                const allLanguages = data.map((lang: { language: string }) => lang.language);
                if (!openedForTranslation) {
                    allLanguages.unshift("Auto");
                }

                setAllLanguages(allLanguages);
                setFilteredLanguages(allLanguages);
            } catch (err) {
                logger.error(err, "Error fetching languages:");
            }
        };

        fetchLanguages();
    }, []);

    const handleSearch = (query: string) => {
        if (!query) {
            setFilteredLanguages(allLanguages);
            setHighlightedLanguage(allLanguages[0]);
            popupRef.current?.closePopup();
            return;
        }

        const lowerCaseQuery = query.toLowerCase();
        setSearchQuery(lowerCaseQuery);

        const filtered = allLanguages.filter(lang => lang.toLowerCase().startsWith(lowerCaseQuery));
        setFilteredLanguages(filtered);
        setHighlightedLanguage(filtered[0]);

        if (filtered.length) {
            popupRef.current?.showPopup();
        } else {
            popupRef.current?.closePopup();
        }
    };

    const handleSearchBlur = (query: string) => {
        if (!openedForTranslation && (!query || !allLanguages.filter(lang => lang.toLowerCase().startsWith(query.toLowerCase())).length)) {
            setShowSearchInput(false);
        }
    };

    const handleSelectedLanguage = (language: string) => {
        onSelectedLanguageChange(language);
        popupRef.current?.closePopup();
        setShowSearchInput(false);
    };

    const handleRemoveSelectedLanguage = () => {
        setSearchQuery("");
        setShowSearchInput(true);
        onSelectedLanguageChange(null);
        setTimeout(() => {
            searchInputRef.current?.focus();
        }, 100)
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        switch (event.which) {
            case Key.UP_ARROW:
                event.preventDefault();
                const prevIndex = filteredLanguages.indexOf(highlightedLanguage || "") - 1;
                if (prevIndex >= 0) {
                    setHighlightedLanguage(filteredLanguages[prevIndex]);
                }
                break;
            case Key.DOWN_ARROW:
                event.preventDefault();
                const nextIndex = filteredLanguages.indexOf(highlightedLanguage || "") + 1;
                if (nextIndex < filteredLanguages.length) {
                    setHighlightedLanguage(filteredLanguages[nextIndex]);
                }
                break;
            case Key.TAB:
            case Key.ENTER:
                event.preventDefault();
                if (highlightedLanguage) {
                    handleSelectedLanguage(highlightedLanguage);
                    setShowSearchInput(false);
                }
                break;
        }
    };

    return (
        <Popup
            ref={popupRef}
            disableOpenOnClick
            disableHoverState
            contained showArrow={false}
            overrideContainerStyles={PopupContainerStyles}
            position={!openedForTranslation ? "right" : "below"}
        >
            <PopupPreview>
                <InputContainer>
                    {showSearchInput && (
                        <TextField
                            inputRef={searchInputRef}
                            value={searchQuery}
                            onChange={handleSearch}
                            onKeyDown={handleKeyDown}
                            onBlur={handleSearchBlur}
                            placeholder={"Search languages..."}
                            InputProps={{ disableUnderline: true }}
                        />
                    )}
                    {!showSearchInput && (
                        <FlexLayout gap={5} align="center" justify={!openedForTranslation ? "space-between" : "flex-start"}>
                            <TextContent {...(!openedForTranslation ? { regular: true } : { popupValue: true })} center>
                                {selectedLanguage || "Auto"}</TextContent>
                            <IconButton size="small" onClick={handleRemoveSelectedLanguage}>
                                <Icon fontSize="small">close</Icon>
                            </IconButton>
                        </FlexLayout>
                    )}
                </InputContainer>
            </PopupPreview>

            <PopupContent>
                {closePopup => (
                    filteredLanguages.map((lang, idx) => (
                        <MenuItem key={idx} value={lang} selected={lang === highlightedLanguage} onClick={() => handleSelectedLanguage(lang)}>
                            {lang}
                        </MenuItem>
                    ))
                )}
            </PopupContent>
        </Popup>
    );
}
